import { useRef, useEffect, useCallback, useReducer } from 'react';
import { ensureAuthenticated } from 'src/utils/auth/authenticate';
import CONSTANTS from 'src/utils/constants';
import AIInsightsService from 'src/services/ai-insights/AIInsightsService';
import { ACTIONS, aiInsightsReducer } from 'src/services/ai-insights/AIInsightsReducer';
import {
    AIInsightsContextValues,
    initialAIInsightsState,
    Message,
} from 'src/services/ai-insights/AIInsightsState';
import {
    AGENT_REPORT_USER_ID,
    AI_INSIGHT_MESSAGE_TYPES,
    REPORT_POLL_INTERVAL_MS,
    REPORT_POLL_TIMEOUT_MS,
    REPORT_READY_FOLLOW_UP_PROMPT,
    REPORT_STATUS_FAILED,
    REPORT_STATUS_READY,
    STREAM_IDLE_COMPLETE_MS,
} from './Constants';

export type { Message } from 'src/services/ai-insights/AIInsightsState';
export type AIInsightContext = AIInsightsContextValues;

/** Matches the report UUID the agent echoes back when it triggers report generation. */
const REPORT_ID_PATTERN = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

/** Words that indicate the agent's reply is announcing a report generation, not just quoting an id. */
const REPORT_GENERATION_HINTS = ['running', 'triggered', 'generation', 'generating'];

/**
 * Resolves the AI Insights WebSocket endpoint. Returns an empty string when the Katal
 * placeholder has not been substituted (i.e. the stage's CDK has not published the
 * CloudFormation output yet), so callers can fail with a clear message instead of
 * attempting to open a socket to a literal "{{...}}" URL.
 */
const resolveWebSocketUrl = (): string => {
    const url = CONSTANTS.ENVIRONMENT_VARIABLES.AI_INSIGHTS_WS_URL;
    if (!url || url.startsWith('{{')) {
        return '';
    }
    return url;
};

/**
 * Drives one AI Insights conversation: opens the agent WebSocket, streams the reply into the
 * transcript, and resumes the conversation once an agent-triggered report finishes generating.
 *
 * State lives in aiInsightsReducer so transitions are explicit and unit-testable. It is
 * intentionally per-instance rather than shared through a context provider — each trigger
 * (a calculation, a report, a variance row) owns an independent conversation.
 */
export default function useAIInsightChat(
    context: AIInsightsContextValues,
    aiInsightsService: AIInsightsService
) {
    const [state, dispatch] = useReducer(aiInsightsReducer, initialAIInsightsState);
    const wsRef = useRef<WebSocket | null>(null);
    const streamingTextRef = useRef('');
    /**
     * Session id is held in a ref rather than state because a message can be sent in the
     * same tick as resetChat() — a state value would still be the previous session's id in
     * that closure and the agent would resume the wrong conversation.
     */
    const sessionIdRef = useRef<string | undefined>(undefined);
    /** Latest context and service, so long-lived callbacks never close over stale values. */
    const contextRef = useRef(context);
    contextRef.current = context;
    const serviceRef = useRef(aiInsightsService);
    serviceRef.current = aiInsightsService;

    const closeSocket = useCallback(() => {
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
    }, []);

    // Close any in-flight socket when the consumer unmounts.
    useEffect(() => closeSocket, [closeSocket]);

    const buildContextPrefix = useCallback(() => {
        const { cliId, reportType, accountingPeriod, accountingDay } = contextRef.current;
        const parts: string[] = [];
        if (cliId) parts.push(`CLI: ${cliId}`);
        if (reportType) parts.push(`Report Type: ${reportType}`);
        if (accountingPeriod) parts.push(`Accounting Period: ${accountingPeriod}`);
        if (accountingDay) parts.push(`Accounting Day: ${accountingDay}`);
        return parts.length > 0 ? `[Context: ${parts.join(', ')}]\n` : '';
    }, []);

    const connectAndSend = useCallback(async (
        question: string,
        options?: { waitForIndexing?: boolean }
    ) => {
        const wsUrl = resolveWebSocketUrl();
        if (!wsUrl) {
            throw new Error('AI Insights is not configured for this environment.');
        }

        const auth = await ensureAuthenticated() as any;
        const idToken = auth?.getSignInUserSession()?.getIdToken()?.getJwtToken();
        if (!idToken) {
            throw new Error('Your session has expired. Refresh the page and try again.');
        }

        return new Promise<void>((resolve, reject) => {
            // API Gateway WebSocket handshakes cannot carry custom headers from the browser, so
            // the id token is passed as a query parameter and validated by the API's request
            // authoriser. Never log this URL. Tracked for replacement with a short-lived
            // exchange token or Sec-WebSocket-Protocol once the agent API supports it.
            const ws = new WebSocket(`${wsUrl}?idToken=${encodeURIComponent(idToken)}`);
            wsRef.current = ws;
            let idleTimeout: number | undefined;
            let settled = false;

            const finish = () => {
                if (settled) return;
                settled = true;
                if (idleTimeout) window.clearTimeout(idleTimeout);
                // COMPLETE_REQUEST rather than SET_LOADING: it also discards a streaming
                // target that never received content, so a turn that produced nothing does
                // not leave a permanently blank reply on screen.
                dispatch(ACTIONS.COMPLETE_REQUEST);
                ws.close();
                resolve();
            };

            /**
             * Starts report polling from whichever signal is available.
             *
             * Both completion paths must run this. The agent may either return a report id on
             * the terminal frame or only announce it in the streamed text, and finish() clears
             * the idle timeout — so if COMPLETE did not also scan the text, a terminal frame
             * without a pendingReportId would cancel text-based detection and polling would
             * silently never start.
             */
            const maybeStartPolling = (reportId?: string): boolean => {
                if (reportId) {
                    dispatch(ACTIONS.START_POLLING.withPayload(reportId));
                    return true;
                }
                const text = streamingTextRef.current;
                const reportIdMatch = text.match(REPORT_ID_PATTERN);
                const lowerText = text.toLowerCase();
                const announcesReport = REPORT_GENERATION_HINTS.some(h => lowerText.includes(h));
                if (reportIdMatch && announcesReport) {
                    dispatch(ACTIONS.START_POLLING.withPayload(reportIdMatch[1]));
                    return true;
                }
                return false;
            };

            ws.onopen = () => {
                ws.send(JSON.stringify({
                    action: 'chat',
                    cliId: contextRef.current.cliId,
                    question: buildContextPrefix() + question,
                    sessionId: sessionIdRef.current,
                    waitForIndexing: options?.waitForIndexing ? 'true' : 'false',
                }));
            };

            ws.onmessage = (event) => {
                let data: any;
                try {
                    data = JSON.parse(event.data);
                } catch (parseError) {
                    console.warn('AI Insights: ignoring unparseable frame', parseError);
                    return;
                }

                // Captured from every frame that carries it, not just COMPLETE: the API does not
                // reliably send a terminal frame, and the idle-timeout and onclose paths would
                // otherwise leave this undefined and silently start a new conversation next turn.
                if (data.sessionId) {
                    sessionIdRef.current = data.sessionId;
                }

                switch (data.type) {
                    case AI_INSIGHT_MESSAGE_TYPES.INDEXING:
                        dispatch(ACTIONS.SET_INDEXING_NOTICE.withPayload(data.text));
                        break;

                    case AI_INSIGHT_MESSAGE_TYPES.CHUNK: {
                        streamingTextRef.current += data.text;
                        // Replaces any indexing notice in the same transition.
                        dispatch(ACTIONS.SET_STREAMING_TEXT.withPayload(streamingTextRef.current));

                        // The API does not reliably send a terminal frame; treat a quiet
                        // period after the last chunk as completion.
                        if (idleTimeout) window.clearTimeout(idleTimeout);
                        idleTimeout = window.setTimeout(() => {
                            maybeStartPolling();
                            finish();
                        }, STREAM_IDLE_COMPLETE_MS);
                        break;
                    }

                    case AI_INSIGHT_MESSAGE_TYPES.COMPLETE: {
                        const polling = maybeStartPolling(data.pendingReportId);
                        // A turn can complete having streamed nothing. When it also triggered
                        // no report there is genuinely no answer, so say so rather than ending
                        // on an empty reply with no explanation.
                        if (!streamingTextRef.current && !polling) {
                            dispatch(ACTIONS.FAIL_REQUEST.withPayload(
                                'The AI Insights agent returned an empty response. Please try asking again.'
                            ));
                        }
                        finish();
                        break;
                    }

                    case AI_INSIGHT_MESSAGE_TYPES.ERROR:
                        // finish() resolves rather than rejects, so send()'s catch never runs;
                        // FAIL_REQUEST also discards the blank streaming bubble so the error is
                        // not rendered beside an empty reply.
                        dispatch(ACTIONS.FAIL_REQUEST.withPayload(
                            data.message || 'The AI Insights agent returned an error.'
                        ));
                        finish();
                        break;

                    default:
                        break;
                }
            };

            ws.onerror = () => {
                if (settled) return;
                settled = true;
                if (idleTimeout) window.clearTimeout(idleTimeout);
                dispatch(ACTIONS.SET_LOADING.withPayload(false));
                reject(new Error('Could not connect to AI Insights. Please try again.'));
            };

            ws.onclose = () => {
                wsRef.current = null;
                if (settled) return;

                // A close is the third way a turn can end, alongside COMPLETE and the idle
                // timeout, and it is not always preceded by onerror — a request authoriser
                // rejecting the idToken closes the socket without one.
                if (streamingTextRef.current) {
                    // Content arrived but no terminal frame; treat the close as completion so a
                    // report announced only in the stream is still picked up for polling.
                    maybeStartPolling();
                } else {
                    // Nothing was ever received. Without this the empty assistant bubble that
                    // BEGIN_REQUEST added would be left on screen with no error and no
                    // indication that the request failed; FAIL_REQUEST discards it.
                    dispatch(ACTIONS.FAIL_REQUEST.withPayload(
                        'The connection to AI Insights closed before a response arrived. Please try again.'
                    ));
                }
                finish();
            };
        });
    }, [buildContextPrefix]);

    const send = useCallback(async (
        question: string,
        options?: { hideUserMessage?: boolean; waitForIndexing?: boolean }
    ) => {
        streamingTextRef.current = '';
        dispatch(ACTIONS.BEGIN_REQUEST.withPayload({
            question, hideUserMessage: options?.hideUserMessage,
        }));

        try {
            await connectAndSend(question, { waitForIndexing: options?.waitForIndexing });
        } catch (ex: any) {
            dispatch(ACTIONS.FAIL_REQUEST.withPayload(ex?.message || 'Failed to generate insight.'));
        }
    }, [connectAndSend]);

    const sendMessage = useCallback((
        question: string,
        options?: { hideUserMessage?: boolean }
    ) => {
        if (!question.trim()) {
            return Promise.resolve();
        }
        return send(question, options);
    }, [send]);

    // Poll for completion of a report the agent triggered, then resume the conversation.
    const { pollingReportId, pollingStartedAt } = state;
    useEffect(() => {
        if (!pollingReportId || !pollingStartedAt) {
            return undefined;
        }

        let cancelled = false;

        const appendAssistant = (text: string) =>
            dispatch(ACTIONS.APPEND_MESSAGE.withPayload({ role: 'assistant', text } as Message));

        const tick = async () => {
            if (cancelled) return;
            const elapsed = Date.now() - pollingStartedAt;
            dispatch(ACTIONS.SET_POLLING_ELAPSED.withPayload(elapsed));

            if (elapsed > REPORT_POLL_TIMEOUT_MS) {
                cancelled = true;
                dispatch(ACTIONS.STOP_POLLING);
                appendAssistant('Report generation is taking longer than expected (>5 min). You can '
                    + `check the reports list directly, or ask me again later using report ID \`${pollingReportId}\`.`);
                return;
            }

            let status: { reportStatus?: string };
            try {
                status = await serviceRef.current.getReportStatus(AGENT_REPORT_USER_ID, pollingReportId);
            } catch (ex) {
                // Transient failures are expected while the report is being generated;
                // keep polling until the timeout above is reached.
                console.warn('AI Insights: getReportStatus failed, will retry', ex);
                return;
            }
            if (cancelled) return;

            const reportStatus = (status?.reportStatus || '').toUpperCase();

            if (REPORT_STATUS_READY.includes(reportStatus)) {
                cancelled = true;
                dispatch(ACTIONS.STOP_POLLING);
                appendAssistant('Report generated successfully. Indexing started...');
                send(REPORT_READY_FOLLOW_UP_PROMPT, { hideUserMessage: true, waitForIndexing: true });
                return;
            }

            if (reportStatus === REPORT_STATUS_FAILED) {
                cancelled = true;
                dispatch(ACTIONS.STOP_POLLING);
                appendAssistant(`Report generation failed (ID: \`${pollingReportId}\`). Please retry, `
                    + 'or check the reports list for details.');
            }
        };

        tick();
        const interval = window.setInterval(tick, REPORT_POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            window.clearInterval(interval);
        };
        // `send` is a stable callback; re-running on it would restart polling.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pollingReportId, pollingStartedAt]);

    const resetChat = useCallback(() => {
        closeSocket();
        sessionIdRef.current = undefined;
        streamingTextRef.current = '';
        dispatch(ACTIONS.RESET_CHAT);
    }, [closeSocket]);

    return {
        messages: state.messages,
        loading: state.loading,
        error: state.error,
        pollingReportId: state.pollingReportId,
        pollingElapsed: state.pollingElapsed,
        sendMessage,
        resetChat,
    };
}
